<template>
  <div style="display: inline;">
  <b-modal id="edit-ticket-details-modal" title="Edit Ticket" size="lg" hide-footer>
    <loading-screen :is-loading="isSaving"></loading-screen>
    <errors :error-list="serverErrors"></errors>

    <div class="form-group mb-4">
      <label for="subject">Subject</label>
      <input
          id="subject"
          name="subject"
          type="text"
          class="form-control"
          v-model="subject"
      />
    </div>

    <div class="form-group mb-4">
      <label for="axum_booking_id">AXUM Booking ID</label>
      <input
          id="axum_booking_id"
          name="axum_booking_id"
          type="text"
          class="form-control"
          v-model="axum_booking_id"
      />
    </div>

    <div class="form-group mb-2">
      <label for="description" class="">Description</label>
      <div class="card shadow-none card-action">
        <div class="card-body pb-0">
          <editor-content :editor="editor"/>
        </div>
        <div class="card-menubar">
          <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
            <div class="menubar">

              <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                 :class="{ 'is-active': isActive.bold() }"
                 @click="commands.bold"
              >
                <i class="fas fa-bold"></i>
              </a>

              <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                 :class="{ 'is-active': isActive.italic() }"
                 @click="commands.italic"
              >
                <i class="fas fa-italic"></i>
              </a>

              <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                 :class="{ 'is-active': isActive.strike() }"
                 @click="commands.strike"
              >
                <i class="fas fa-strikethrough"></i>
              </a>

              <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                 :class="{ 'is-active': isActive.underline() }"
                 @click="commands.underline"
              >
                <i class="fas fa-underline"></i>
              </a>

              <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                 :class="{ 'is-active': isActive.bullet_list() }"
                 @click="commands.bullet_list"
              >
                <i class="fas fa-list-ul"></i>
              </a>

              <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                 :class="{ 'is-active': isActive.ordered_list() }"
                 @click="commands.ordered_list"
              >
                <i class="fas fa-list-ol"></i>
              </a>

              <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                 @click="commands.undo"
              >
                <i class="fas fa-undo"></i>
              </a>

              <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                 @click="commands.redo"
              >
                <i class="fas fa-redo"></i>
              </a>
            </div>
          </editor-menu-bar>
        </div>
      </div>
    </div>

    <button type="submit" class="btn btn-itg-dark" :disabled="isSaving || isLoadingAxum"
            @click.prevent="save">
      <b-spinner small v-if="isLoadingAxum" class="mr-1"></b-spinner>
      {{ isLoadingAxum ? 'Loading...' : 'Save' }}
    </button>
  </b-modal>

  <!-- Subject Suggestion Modal -->
  <b-modal 
    title="Update Subject with Booking ID?" 
    centered
    v-model="showSubjectSuggestionModal"
    @hidden="closeSubjectSuggestionModal"
  >
    <!-- 1. Current subject -->
    <div class="mb-3">
      <label class="font-weight-bold">Current subject:</label>
      <p class="text-muted">{{ subject }}</p>
    </div>
    
    <!-- 2. Proposed subject (static text) -->
    <div class="mb-3">
      <label class="font-weight-bold">Proposed subject:</label>
      <p class="text-muted">{{ suggestedSubject }}</p>
    </div>
    
    <!-- 3. Radio input -->
    <div class="mb-3">
      <div class="form-check mb-2">
        <input
          class="form-check-input"
          type="radio"
          name="subjectAction"
          id="acceptSuggestion"
          value="accept"
          v-model="subjectAction"
        />
        <label class="form-check-label" for="acceptSuggestion">
          Accept Suggestion
        </label>
      </div>
      <div class="form-check">
        <input
          class="form-check-input"
          type="radio"
          name="subjectAction"
          id="editAndAccept"
          value="edit"
          v-model="subjectAction"
        />
        <label class="form-check-label" for="editAndAccept">
          Edit and Accept
        </label>
      </div>
    </div>

    <!-- 4. Input for edit suggestion (only shown if "Edit and Accept" is selected) -->
    <div class="mb-3" v-if="subjectAction === 'edit'">
      <label class="font-weight-bold">Edit subject:</label>
      <input
        type="text"
        class="form-control"
        v-model="editableSuggestedSubject"
        ref="suggestedSubjectInput"
      />
    </div>

    <!-- TC Change Preview (show when booking ID changed) -->
    <div class="mb-3" v-if="newAxumData || axumError || isLoadingAxum">
      <label class="font-weight-bold">Travel Consultant:</label>

      <!-- Loading state -->
      <div v-if="isLoadingAxum" class="text-center py-2">
        <b-spinner small variant="itg-dark"></b-spinner>
      </div>

      <!-- Success: TC Preview with change indicator -->
      <div v-else-if="newAxumData && newAxumData.consultant">
        <div class="d-flex align-items-center mt-1">
          <div class="text-muted mr-2">
            {{ ticket.travel_consultant_name || 'None' }}
          </div>
          <i class="fas fa-arrow-right mx-2"></i>
          <div class="font-weight-bold text-success">
            {{ newAxumData.consultant.username }}
          </div>
        </div>
        <small class="text-muted">{{ newAxumData.consultant.emailAlias }}</small>

        <!-- Checkbox to opt-in/out of TC update -->
        <div class="form-check mt-2">
          <input
            class="form-check-input"
            type="checkbox"
            id="updateTcCheckbox"
            v-model="updateTravelConsultant"
          />
          <label class="form-check-label" for="updateTcCheckbox">
            Update travel consultant
          </label>
        </div>
      </div>

      <!-- Error state: AXUM failed but save still works -->
      <div v-else-if="axumError">
        <div class="text-warning">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          {{ axumError }}
        </div>
        <small class="text-muted">
          Current TC ({{ ticket.travel_consultant_name || 'None' }}) will be kept.
        </small>
      </div>

      <!-- No consultant in response -->
      <div v-else-if="newAxumData && !newAxumData.consultant">
        <div class="text-muted">No consultant found for this booking</div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button 
        type="button" 
        class="btn btn-itg mr-2"
        @click="makeNoChanges"
      >
        Make No Changes
      </button>
      <button 
        type="button" 
        class="btn btn-itg-dark"
        @click="saveWithChanges"
        :disabled="!subjectAction"
      >
        Save with Changes
      </button>
    </template>
  </b-modal>
  </div>
</template>

<script>
import {mapGetters, mapActions} from "vuex";
import Forms from "../../../../../mixins/Forms";
import {Editor, EditorContent, EditorMenuBar} from 'tiptap';
import {
  Blockquote, Bold,
  BulletList, Code,
  CodeBlock,
  HardBreak,
  Heading, History, Italic, Link,
  ListItem,
  OrderedList, Strike,
  TodoItem, TodoList, Underline
} from "tiptap-extensions";
import axios from 'axios';

export default {
  name: "EditTicketDetailsModal",
  components: {Editor, EditorContent, EditorMenuBar},
  mixins: [Forms],
  data() {
    return {
      isSaving: false,
      serverErrors: [],

      subject: null,
      previous_axum_booking_id: null,
      axum_booking_id: null,
      editor: null,

      showSubjectSuggestionModal: false,
      suggestedSubject: null,
      editableSuggestedSubject: null,
      subjectAction: null,

      // TC update state
      isLoadingAxum: false,
      newAxumData: null,
      axumError: null,
      updateTravelConsultant: true
    }
  },
  mounted() {
    this.setupEditor();
  },
  created() {
    this.subject = this.ticket.subject;
    this.previous_axum_booking_id = this.ticket.axum_booking_id;
    this.axum_booking_id = this.ticket.axum_booking_id;
  },
  watch: {
    ticket() {
      if (this.ticket) {
        this.axum_booking_id = this.ticket.axum_booking_id;
        this.previous_axum_booking_id = this.ticket.axum_booking_id;
        this.subject = this.ticket.subject;
        this.editor.commands.setContent(this.ticket.description);
      }
    },
    subjectAction(newValue) {
      if (newValue === 'edit') {
        // Ensure editableSuggestedSubject is set when switching to edit mode
        if (!this.editableSuggestedSubject) {
          this.editableSuggestedSubject = this.suggestedSubject;
        }
        // Focus the input field
        this.$nextTick(() => {
          if (this.$refs.suggestedSubjectInput) {
            this.$refs.suggestedSubjectInput.focus();
          }
        });
      }
    }
  },
  computed: {
    ...mapGetters('ticketViewer', {
      ticket: 'ticket'
    })
  },
  methods: {
    ...mapActions('ticketViewer', {
      reloadTicket: 'reloadTicket'
    }),

    setupEditor() {
      let vm = this;
      this.editor = new Editor({
        extensions: [
          new Blockquote(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({levels: [1, 2, 3]}),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new TodoItem(),
          new TodoList(),
          new Bold(),
          new Code(),
          new Italic(),
          new Link(),
          new Strike(),
          new Underline(),
          new History(),
        ],
        content: this.ticket.description
      })
    },

    save() {
      if (this.isSaving) {
        return false;
      }

      // Check if we should show the subject suggestion modal
      const trimmedBookingId = (this.axum_booking_id || '').trim();
      const bookingIdChanged = trimmedBookingId &&
                               trimmedBookingId !== (this.previous_axum_booking_id || '').trim();

      if (bookingIdChanged) {
        // Fetch AXUM data and show modal
        this.fetchAxumDataAndShowModal(trimmedBookingId);
        return;
      }

      // Proceed with save if no modal needed
      this.performSave();
    },

    async fetchAxumDataAndShowModal(bookingId) {
      this.isLoadingAxum = true;
      this.axumError = null;
      this.newAxumData = null;
      this.updateTravelConsultant = true;

      try {
        const response = await axios.get(API_BASE + '/axum-search/' + bookingId);
        this.newAxumData = response.data;
      } catch (e) {
        // AXUM failed - that's OK, we'll still allow save
        this.axumError = 'Could not load booking details - TC will not be updated';
        this.newAxumData = null;
        this.updateTravelConsultant = false;
      }

      this.isLoadingAxum = false;

      // Prepare subject suggestion (works independent of AXUM)
      if (this.shouldUpdateSubject(bookingId)) {
        this.suggestedSubject = this.generateSuggestedSubject(bookingId);
        this.editableSuggestedSubject = this.suggestedSubject;
        this.subjectAction = 'accept';
      }

      // Always show the modal - even if AXUM failed, user can still save
      this.showSubjectSuggestionModal = true;
    },

    shouldUpdateSubject(newBookingId) {
      if (!this.subject || !newBookingId) {
        return false;
      }

      // Check if subject already starts with the new booking ID
      const subjectStartsWithBookingId = this.subject.trim().startsWith(newBookingId + ' - ');
      
      return !subjectStartsWithBookingId;
    },

    generateSuggestedSubject(newBookingId) {
      if (!this.subject) {
        return newBookingId;
      }

      // Pattern: "number - rest of subject"
      const pattern = /^\d+\s*-\s*(.+)$/;
      const match = this.subject.match(pattern);

      if (match) {
        // Subject already has a booking number pattern, replace it
        return newBookingId + ' - ' + match[1];
      } else {
        // Subject doesn't have booking number, prepend it
        return newBookingId + ' - ' + this.subject.trim();
      }
    },

    saveWithChanges() {
      if (!this.subjectAction) {
        return;
      }

      if (this.subjectAction === 'accept') {
        // Use the original suggestion without any edits
        this.subject = this.suggestedSubject;
      } else if (this.subjectAction === 'edit') {
        // Use the edited value from the input field
        this.subject = this.editableSuggestedSubject;
      }

      this.showSubjectSuggestionModal = false;
      this.performSave();
    },

    makeNoChanges() {
      this.showSubjectSuggestionModal = false;
      this.performSave();
    },

    resetSuggestionModalState() {
      this.subjectAction = null;
      this.editableSuggestedSubject = null;
      this.newAxumData = null;
      this.axumError = null;
      this.updateTravelConsultant = true;
    },

    closeSubjectSuggestionModal() {
      this.showSubjectSuggestionModal = false;
      this.resetSuggestionModalState();
    },

    performSave() {
      let vm = this;
      vm.isSaving = true;

      const payload = {
        subject: vm.subject,
        axum_booking_id: vm.axum_booking_id,
        description: vm.editor.getHTML()
      };

      // Add TC fields ONLY if:
      // 1. Checkbox is checked
      // 2. We successfully fetched AXUM data
      // 3. The data contains consultant info
      if (vm.updateTravelConsultant &&
          vm.newAxumData &&
          vm.newAxumData.consultant) {
        payload.travel_consultant_name = vm.newAxumData.consultant.username || null;
        payload.travel_consultant_email = vm.newAxumData.consultant.emailAlias || null;
      }

      axios.put(API_BASE + '/tickets/' + this.ticket.id, payload)
        .then(r => {
          vm.isSaving = false;
          vm.reloadTicket();
          vm.$bvModal.hide('edit-ticket-details-modal');
          vm.resetSuggestionModalState();
        }).catch(e => {
          vm.isSaving = false;
          vm.setErrors(e);
          vm.resetSuggestionModalState();
        });
    }
  }
}
</script>

<style scoped>

</style>
